import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { VisuallyHidden } from '@artosera/ui';
import { TableauDefilant } from '../../../../components/TableauDefilant.js';
import { totauxRecette } from '../../../../lib/recette.js';
import type { LigneRecette, RecetteOuverture } from '../../../../types.js';
import pageStyles from '../../../../styles/page.module.css';
import tableStyles from '../../../../styles/table.module.css';
import styles from './Recette.module.css';

const NOMBRE = new Intl.NumberFormat('fr-FR');

/** Un nombre de colonne : alignés à droite, chiffres tabulaires. */
function Chiffre({ valeur }: { valeur: number }): JSX.Element {
  return <td className={styles.num}>{NOMBRE.format(valeur)}</td>;
}

function CelluleEcart({ ecart }: { ecart: number }): JSX.Element {
  const { t } = useTranslation('console');
  return (
    <td className={`${styles.num} ${styles.ecart}`}>
      {ecart === 0 ? (
        <span className={styles.zero}>
          0<VisuallyHidden> ({t('ouvertures.fiche.recette.tableau.ecartNul')})</VisuallyHidden>
        </span>
      ) : (
        <strong className={styles.ecartValeur}>{NOMBRE.format(ecart)}</strong>
      )}
    </td>
  );
}

/**
 * Le tableau de contrôle par nature (spec § 8.11) : total de la source,
 * créés, complétés, ignorés, refusés, écartés, et l'écart qui reste à
 * expliquer. Une nature dont l'écart n'est pas nul se repère à sa bande
 * rouge et montre ses motifs d'écartement sous sa ligne ; le pied additionne
 * chaque colonne et ferme le compte avec la même règle.
 */
export function TableauControle({ recette }: { recette: RecetteOuverture }): JSX.Element {
  const { t } = useTranslation('console');
  const cle = 'ouvertures.fiche.recette.tableau';
  const { natures } = recette;
  const totaux = totauxRecette(natures);
  const enEcart = natures.filter((ligne) => ligne.ecart !== 0).length;

  return (
    <section className={pageStyles.panneau} aria-labelledby="rec-tableau">
      <div className={pageStyles.sectionTitre}>
        <h2 id="rec-tableau">{t(`${cle}.titre`)}</h2>
      </div>
      <p className={styles.texte}>{t(`${cle}.texte`)}</p>

      {enEcart > 0 ? (
        <p className={styles.alerte} role="status">
          {t(`${cle}.alerte`, { count: enEcart })}
        </p>
      ) : null}

      {natures.length === 0 ? (
        <p className={pageStyles.videEtat}>{t(`${cle}.vide`)}</p>
      ) : (
        <TableauDefilant libelle={t(`${cle}.libelle`)}>
          <table className={`${tableStyles.table} ${styles.controle}`}>
            <thead>
              <tr>
                <th scope="col">{t(`${cle}.colonnes.nature`)}</th>
                {(
                  ['lots', 'total', 'crees', 'completes', 'ignores', 'refuses', 'ecartes'] as const
                ).map((colonne) => (
                  <th key={colonne} scope="col" className={styles.num}>
                    {t(`${cle}.colonnes.${colonne}`)}
                  </th>
                ))}
                <th scope="col" className={styles.num}>
                  {t(`${cle}.colonnes.ecart`)}
                </th>
                <th scope="col" className={styles.num}>
                  {t(`${cle}.colonnes.aRevoir`)}
                </th>
              </tr>
            </thead>
            <tbody>
              {natures.map((ligne) => (
                <LigneNature key={ligne.nature} ligne={ligne} />
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th scope="row">{t(`${cle}.total`)}</th>
                <Chiffre valeur={totaux.lots} />
                <Chiffre valeur={totaux.total} />
                <Chiffre valeur={totaux.crees} />
                <Chiffre valeur={totaux.completes} />
                <Chiffre valeur={totaux.ignores} />
                <Chiffre valeur={totaux.refuses} />
                <Chiffre valeur={totaux.ecartes} />
                <CelluleEcart ecart={totaux.ecart} />
                <Chiffre valeur={totaux.aRevoir} />
              </tr>
            </tfoot>
          </table>
        </TableauDefilant>
      )}

      {recette.aRevoir > 0 ? (
        <p className={`${styles.discret} ${styles.titreBloc}`}>
          {t(`${cle}.aRevoir`, { count: recette.aRevoir })}
        </p>
      ) : null}
    </section>
  );
}

function LigneNature({ ligne }: { ligne: LigneRecette }): JSX.Element {
  const { t } = useTranslation('console');
  const cle = 'ouvertures.fiche.recette.tableau';
  const enEcart = ligne.ecart !== 0;
  const aDesMotifs = ligne.motifs.length > 0;
  return (
    <>
      <tr data-ecart={enEcart} data-nature={ligne.nature}>
        <th scope="row">{t(`reprise.natures.${ligne.nature}`, { defaultValue: ligne.nature })}</th>
        <Chiffre valeur={ligne.lots} />
        <Chiffre valeur={ligne.total} />
        <Chiffre valeur={ligne.crees} />
        <Chiffre valeur={ligne.completes} />
        <Chiffre valeur={ligne.ignores} />
        <Chiffre valeur={ligne.refuses} />
        <Chiffre valeur={ligne.ecartes} />
        <CelluleEcart ecart={ligne.ecart} />
        <Chiffre valeur={ligne.aRevoir} />
      </tr>
      {enEcart || aDesMotifs ? (
        <tr className={styles.motifsLigne} data-ecart={enEcart}>
          <td colSpan={10}>
            <div className={styles.motifs}>
              {enEcart ? <p>{t(`${cle}.ecartNonNul`, { count: Math.abs(ligne.ecart) })}</p> : null}
              {aDesMotifs ? (
                <>
                  <span className={tableStyles.celluleMuette}>{t(`${cle}.motifs`)}</span>
                  <ul>
                    {ligne.motifs.map((motif) => (
                      <li key={motif}>{motif}</li>
                    ))}
                  </ul>
                </>
              ) : (
                <span>{t(`${cle}.sansMotif`)}</span>
              )}
            </div>
          </td>
        </tr>
      ) : null}
    </>
  );
}
